<template lang="pug">
	.creative--text-based-audio-editor
		.flex-row(:class="{ 'disabled-controls': disabled }")
			.flex-col.space-medium.flex-2
				.flex-col.space.text-panel
					.flex-row.justify.space
						.flex-row.space.align-center.flex-1
							slot(name="actions-left")
							.creative--text-based-audio-editor--player.flex-1(ref="player", :style="{ '--width': `${0}%` }", @click="onPlayerClick")
								.player-line
								.timers.flex-row.space-small
									span(ref="playingTime") {{$Data.secondsToDisplayValue(0)}}
									span /
									span {{$Data.secondsToDisplayValue(totalDuration)}}
						.flex-row.space.align-center
							slot(name="actions-right")
							AudioTrackerButton(v-if="!readonly && isDebug", icon="fas fa-arrow-right-from-bracket", :title="$tCaption('Show start position in audio')", :disabled="!highlightedWords.length", @click="showPositionInAudio(-1)")
							AudioTrackerButton(v-if="!readonly && isDebug", icon="fas fa-arrow-right-to-bracket", :title="$tCaption('Show end position in audio')", :disabled="!highlightedWords.length", @click="showPositionInAudio(1)")
							AudioTrackerButton(v-if="!readonly", icon="fas fa-trash", :title="$tCaption('Remove')", :disabled="!highlightedWords.length", @click="removeHighlightedWords")
							AudioTrackerButton(v-if="!readonly", icon="fas fa-pencil", :title="$tCaption('Edit')", :disabled="!(isHighlightedCloseWords && highlightedWords.length > 0)", @click="editHighlightedWords")
							AudioTrackerButton(v-if="!readonly", icon="fas fa-close", :title="$tCaption('Clear selected text')", :disabled="!highlightedWords.length", @click="clearHighlight")
							DxDropDownButton(v-if="!readonly", :items="insertActions", stylingMode="text", icon="plus", :hint="$tCaption('Selection actions')", :dropDownOptions="dropDownOptions", 
								keyExpr="id", displayExpr="text", :disabled="highlightedWords.length !== 1", :showArrowIcon="false")
							DxDropDownButton(v-if="!readonly", :items="actions", stylingMode="text", width="120", :text="$tCaption('Actions')", :dropDownOptions="dropDownOptions", keyExpr="id", displayExpr="text")
					DxDataGrid(ref="grid", :height="historyHeight", :columns="columns", :data-source="visibleSentences", :showColumnHeaders="false", keyExpr="key", :scrolling="scrolling")
						template(v-slot:sentence="{ data }")
							.sentence.flex-row.space.justify.wrap(:style="{ '--speaker-color': getSpeakerColor(data.data.speaker) }")
								.flex-row.space-medium.wrap.words
									.speaker.clickable.hoverable(@click="showSpeakerPopup(data.data.speaker)") {{getSpeakerName(data.data.speaker)}}:
									.flex-row.space.wrap
										.flex-row.space.align-center.word(v-for="word in data.data.words.filter(f => !f.removed)", @mousedown.prevent="e => setHighlightMode(word, e)", @mouseup="setHighlightMode(null)", @mouseenter.stop="checkHighlight(word)",
											:class="{ 'mark-to-remove': word.markToRemove, 'highlighted': isWordSelected(word), 'disabled-controls': word.isProcessing, 'custom': word.CustomBuffer }", 
											:key="word.groupKey", @mousemove.prevent="", @dblclick="e => onWordDblClick(word)", :id="`word_${word.groupKey}`")
											span {{word.SentenceWord}}
											fas(v-if="word.isProcessing", icon="spinner", :spin="true")
								.sentence-action-holder
									.flex-row.space.align-center.sentence-actions
										.action-button.grey.flex-row.hoverable.clickable.align-center.center.space(v-if="!readonly", @click="removeSentence(data.data)")
											fas(icon="trash")
					.totals.flex-row.space-medium.font-mini
						.flex-row.space-small.grey
							span {{totalWords}} {{$tCaption('word(s)')}},
							span {{$formatNumber(totalDuration, 1).replace(',', '.')}} sec.
			.flex-col.space-large.flex-1.commands-panel
				.flex-row.align-center.space(ref="sentencesList", :class="{ 'disabled-controls': isProcessing || readonly }")
					DxTextBox.flex-1(:value.sync="command", :placeholder="$tCaption('Enter any text processing description')", stylingMode="filled", :onEnterKey="processGPT", valueChangeEvent="input")
					.send-button.flex-row.hoverable.clickable.align-center.center(@click="processGPT", :class="{ 'disabled-controls': !command }")
						fas(:icon="isProcessing ? 'spinner' : 'share'", :spin="!!isProcessing")
				DxScrollView.scroll-view(showScrollbar="always", :height="historyHeight / (isDebug ? 2 : 1) - (isDebug ? 50 : 0)")
					.flex-col.space-medium
						.history.flex-col(v-if="!readonly", v-for="(item, itemIdx) in history")
							.font-small {{$moment(item.time).format('HH:mm:ss')}}: {{item.command ? `"${getShortened(item.command)}"` : getShortened(item.action || item.text)}}
							.flex-row.space.align-center
								template(v-if="!item.confirmed")
									.action-button.success.flex-row.hoverable.clickable.align-center.center.space(@click="confirmChange(item)")
										fas(icon="check")
										.font-small {{$tCaption('Apply')}}
									.action-button.error.flex-row.hoverable.clickable.align-center.center.space(@click="deleteChange(item, itemIdx)")
										fas(icon="times")
										.font-small {{$tCaption('Reject')}}
								template(v-else)
									.action-button.grey.flex-row.hoverable.clickable.align-center.center.space(@click="revertChange(item, itemIdx)")
										fas(icon="rotate-left", size="sm")
										.font-small {{$tCaption('Revert changes')}}
				.flex-col.space(v-if="isDebug")
					.flex-row.space.justify.font-small
						.upper.grey Debug log:
						.flex-row.align-center.space-medium
							.clickable.hoverable(@click="showSpeechResponse") Speech response
							.clickable.hoverable(@click="showSpeechResponseCSV") (CSV)
							.clickable.hoverable(@click="debugInfo = []") Clear
					DxScrollView.scroll-view(v-if="isDebug", showScrollbar="always", :height="historyHeight / (isDebug ? 2 : 1)")
						.flex-col.space-medium
							.debug-log.flex-col.space.font-tiny(v-for="log in debugInfo")
								.flex-row.space.wrap
									.grey {{$moment(log.time).format('HH:mm:ss')}} 
									span {{log.title}}
								.flex-row.space.wrap
									.flex-row.space-small(v-for="action in log.actions")
										.debug-action.clickable.hoverable.flex-row.space(@click="executeDebugAction(action)")
											span {{action.name}}
										.debug-action.clickable.hoverable.flex-row.space.align-center.center(v-if="action.audio", @click="downloadDebugAction(action, log)")
											fas(icon="download")
		Loader(v-if="isLoading || isCorrectingTimings", :text="isCorrectingTimings ? $tCaption('Processing audio: VALUE%').replace(/VALUE/g, correctingProgress) : $tCaption('Loading', '@actions')", :fullscreen="true")
		Popup(v-if="overdubbingVisible", :popupVisible.sync="overdubbingVisible", :width="320", :autosize="true", :title="overdubbingTitle")
			.flex-col.space
				DxTextBox(:value.sync="overdubbingWord", valueChangeEvent="input", :placeholder="$tCaption('Write something here..')")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="overdubbingVisible = false")
				DxButton(:text="$tCaption('OK', '@actions')", @click="processOverdub", :disabled="!overdubbingWord", type="default")
		Popup(v-if="speakerPopup.visible", :popupVisible.sync="speakerPopup.visible", :width="320", :autosize="true", :title="$tCaption('Edit speaker')")
			.flex-col.space-medium
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Name')}}
					DxTextBox(:value.sync="speakerPopup.name", valueChangeEvent="input", :placeholder="$tCaption('Write something here..')", stylingMode="filled")
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Color')}}
					DxColorBox(:value.sync="speakerPopup.color",applyValueMode="instantly", stylingMode="filled")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="speakerPopup.visible = false")
				DxButton(:text="$tCaption('Save', '@actions')", @click="saveSpeaker", :disabled="!speakerPopup.name", type="success")
		BottomAudioPlayer(ref="bottomPlayer", :visible="!!debugAudio", :src="debugAudio", @hide="debugAudio = null", @initiated="onWaveformInitiated", :title="debugAudioTitle", :markers="debugMarkers")
			.creative--text-based-audio-editor.flex-row.space-large(slot="actions-left")
				.flex-row.space-medium
					.flex-row.space(v-if="isDebug && highlightedWords.length")
						span.nowrap AVG Multiplier:
						input.small-input(v-model="avgStep", :style="{ 'width': '80px' }")
					a.justify.flex-row.align-center-center(v-if="isDebug && highlightedWords.length", @click="moveAvg(-1 * avgStep)", :title="'Move to -AVG * Multiplier'")
						span A-
					a.justify(v-if="isDebug && highlightedWords.length", @click="showPositionInAudio(-1)", :title="'Set word start'")
						fas(icon="arrow-right-from-bracket")
					a.justify(v-if="isDebug && highlightedWords.length", @click="showPositionInAudio(1)", :title="'Set word end'")
						fas(icon="arrow-right-to-bracket")
					a.justify.flex-row.align-center-center(v-if="isDebug && highlightedWords.length", @click="moveAvg(avgStep)", :title="'Move to +AVG * Multiplier'")
						span A+
					a.justify(v-if="isDebug && highlightedWords.length", @click="gotoDebug(2)", :title="'Avg window start'") WS
					a.justify(v-if="isDebug && highlightedWords.length", @click="gotoDebug(3)", :title="'Avg window end'") WE
					a.justify(v-if="isDebug && highlightedWords.length", @click="gotoDebug(4)", :title="'Peak start'") PS
					a.justify(v-if="isDebug && highlightedWords.length", @click="gotoDebug(5)", :title="'Peak end'") PE
					a.justify.flex-row.align-center-center(v-if="isDebug && highlightedWords.length", @click="apply(-1)", :title="'Save current position as word start'")
						span S*
					a.justify.flex-row.align-center-center(v-if="isDebug && highlightedWords.length", @click="apply(1)", :title="'Save current position as word end'")
						span E*
					a.justify.flex-row.align-center-center(v-if="isDebug && highlightedWords.length", @click="apply(0)", :title="'Restore original positions'")
						span R*
		BottomPanel(v-if="!disabled", ref="bottomPanel", :visible="bottomPanelVisible", :src="debugAudio", @hide="debugAudio = null", :duration="totalDuration",
			:height="bottomPanelHeightProp.value", :groups="groups" :words="words", @change="build", :isPlaying="isPlaying", :buffer="buffer", @height-change="v => bottomPanelHeightProp.value = v",
			:getSpeakerColor="getSpeakerColor", :sonicScribeSplitGroup="sonicScribeSplitGroup")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import { bufferToWave } from 'app-core/audio';
	import { saveAs } from 'file-saver';
	import { DxDropDownButton } from 'devextreme-vue/drop-down-button';
	import audioHelper from 'view/audioTracker/audio-helper';
	import audio from 'app-core/audio';
	import Popup from 'app-core/Popup.vue';
	import ssmlHelper from 'view/insertion/ssml-helper';
	import ssmlGenerator from './ssml-generator';
	import { DxDataGrid } from 'devextreme-vue/data-grid';
	import { initializeSonicScribeGroupWord, buildGroups, initStructureSentences, getWordsForHash, initGroups } from './sonic-scribe';
	import BottomAudioPlayer from 'view/audioPlayer/BottomAudioPlayer.vue';
	import { calculateBPM } from 'view/audioTracker/beat-matching';
	import AudioTrackerButton from 'view/audioTracker/AudioTrackerButton.vue';
	import BottomPanel from './SonicScribe/BottomPanel.vue';
	import storage from 'storage';
	import DxColorBox from 'devextreme-vue/color-box';
	import sonicScribeMixin from './sonic-scribe';
	import { correctWordTimings } from './SonicScribe/words-detection.js';

	export default {
		translationCategory: 'creative.CreativeTextBaseAudioEditor',
		name: 'creative--text-based-audio-editor',
		mixins: [sonicScribeMixin],
		components: {
			Loader, DxTextBox, DxButton, DxScrollView, DxDropDownButton, Popup, DxDataGrid, BottomAudioPlayer, AudioTrackerButton, BottomPanel, DxColorBox
		},
		props: ['track', 'height', 'heightAuto', 'structureStoreMode', 'creative', 'structure', 'readonly'],
		data() {
			return {
				isLoading: 0,
				isProcessing: 0,
				disabled: false,				
				command: null,
				history: [],
				isPlaying: false,
				playStarted: 0,
				totalDuration: 0,
				totalWords: 0,
				actions: [
					{
						text: this.$tCaption('Remove filler words'),
						onClick: this.removeFillerWords,
						icon: 'clear'
					},
					{
						text: this.$tCaption('Download audio'),
						onClick: () => this.download(),
						icon: 'download'
					}
				],
				insertActions: [
					{
						text: this.$tCaption('Insert word before'),
						onClick: () => this.showOverdubbingWindow('before'),
						icon: 'fas fa-indent'
					},
					{
						text: this.$tCaption('Insert word after'),
						onClick: () => this.showOverdubbingWindow('after'),
						icon: 'fas fa-indent scaleX-reversed'
					}
				],
				dropDownOptions: {
					minWidth: '220px'
				},
				overdubbingWord: null,
				overdubbingMode: null,
				overdubbingVisible: false,
				innerHeight: 0,
				columns: [{ dataField: 'key', cellTemplate: 'sentence' }],
				scrolling: {
					mode: 'virtual',
					scrollByContent: true,
					rowRenderingMode: 'virtual'
				},
				isBuilding: false,
				debugInfo: [],
				debugAudio: null,
				speakerPopup: {
					id: null,
					name: null,
					visible: false,
					color: null
				},
				debugAudioTitle: null,
				avgStep: 0.5,
				debugMarkers: null,
				bottomPanelHeightProp: storage.prop('sonicScribe--bottomPanelHeight', void 0, 140),
				buffer: null,
				selectedWordKeys: [],
				isCorrectingTimings: false,
				correctingProgress: 0
			};
		},
		computed: {
			historyHeight: {
				get: function() {
					return Math.max(220, this.height || this.innerHeight);
				},
				cache: false
			},
			overdubbingTitle() {
				if (this.overdubbingMode === 'before') return this.$tCaption('Insert before');
				if (this.overdubbingMode === 'after') return this.$tCaption('Insert after');
				return this.$tCaption('Replace word with');
			},
			highlightedWords() {
				return this.words.filter(f => this.isWordSelected(f));
			},
			visibleSentences() {
				return this.sentences.filter(f => !f.removed);
			},
			isDebug() {
				return this.$route?.query?.debug || false;
			},
			isHighlightedCloseWords() {
				let result = true;

				let curIdx = -1;
				this.highlightedWords.map(f => {
					if (curIdx >= 0) {
						if (Math.abs(curIdx - this.words.indexOf(f)) > 1) {
							result = false;
						}
					}
					curIdx = this.words.indexOf(f);
				});

				return result;
			},
			speakers: {
				get: function () { 
					let v = this.sonicScribeInfo('speakers');
					if (!v) {
						v = {};
						this.sonicScribeInfo('speakers', v);
					}
					return v;
				},
				set: function (v) { this.sonicScribeInfo('speakers', v) }
			},
			avgWordLen() {
				return this.round(_.sum(this.words.map(f => f.Duration)) / this.words.length);
			},
			bottomPanelVisible() {
				return this.words?.length > 0;
			},
			words() {
				return this.sentences.reduce((res, s) => [...res, ...s.words], []);
			},
			sentences() {
				let result = [];

				this.groups.map(group => {
					group?.structure?.map(sentence => {
						let newSentence = {
							...sentence,
							index: result.length,
							group
						}
						let words = this.getGroupWords(group, sentence.words);
						if (words.length) {
							newSentence.words = words;
							result.push(newSentence);
						}
					});
				});

				return result;
			},
			groups() {
				return (this.sonicScribeInfo('groups')?.items || []);
			}
		},
		watch: {
			isPlaying(v) {
				this.$emit('playing', v);
			},
			history: {
				handler() {
					this.$emit('data-changed');
				},
				deep: true
			},
			disabled(v) {
				this.$emit('disabled', v || !!this.isLoading);
			},
			isLoading(v) {
				this.$emit('disabled', v || !!this.disabled);
			},
			sentences: {
				handler(v) {
					this.$emit('structure-changed', v);
				},
				deep: true
			},
			isBuilding(v) {
				this.$emit('building', v);
			},
			words: {
				handler() {
					this.$build();
				},
				deep: true
			},
			highlightedWords() {
				if (this.debugAudio) {
					this.showPositionInAudio(-1);
				}
			},
			avgStep() {
				this.setDebugMarkers(true);
			},
			'bottomPanelHeightProp.value'() {
				this.calcHeight();
			},
			'creative.JsonConfig.sonicScribe': {
				handler() {
					this.creative.__saveJson();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.creativeTextBaseAudioEditor = this;
			this.init();
			this.calcHeight();

			document.body.addEventListener('mouseup', this.onBodyMouseUp);

			this.$build = _.debounce(this.build, 1000, {
				leading: false,
				trailing: true
			});

			this.$eventsBus.$on('sonic-scribe-timeline', this.onSonicScribeTimeline);
			this.$eventsBus.$on('sonic-scribe-select-word', this.onSonicScribeSelectWord);
			this.$eventsBus.$on('sonic-scribe-word-dbl-click', this.onWordDblClick);
			this.$eventsBus.$on('sonic-scribe-clear-selection', this.onSonicScribeClearSelection);
			this.$eventsBus.$on('sonic-scribe-select-by-groupkeys', this.onSonicScribeSelectByGroupKeys);
			this.$eventsBus.$on('sonic-scribe-add-to-history', this.onSonicScribeAddToHistory);
		},
		destroyed() {
			this.stop();
			delete this.$highlightMode;
			delete this.$playingData;
			delete this.$lastBuildHash;
			delete this.$lastBuildResult;
			delete this.$build;
			delete this.$waveFormPosition;
			delete this.$lastWaveFormPositionWord;
			delete this.$debugBuffer;
			delete this.$waveFormStartDelta;
			delete this.$waveFormStart;

			document.body.removeEventListener('mouseup', this.onBodyMouseUp);
			this.$eventsBus.$off('sonic-scribe-timeline', this.onSonicScribeTimeline);
			this.$eventsBus.$off('sonic-scribe-select-word', this.onSonicScribeSelectWord);
			this.$eventsBus.$off('sonic-scribe-word-dbl-click', this.onWordDblClick);
			this.$eventsBus.$off('sonic-scribe-clear-selection', this.onSonicScribeClearSelection);
			this.$eventsBus.$off('sonic-scribe-select-by-groupkeys', this.onSonicScribeSelectByGroupKeys);
			this.$eventsBus.$off('sonic-scribe-add-to-history', this.onSonicScribeAddToHistory);
		},
		methods: {
			addToHistory(data) {
				data.time = new Date();
				this.history.splice(0, 0, data);
			},
			onSonicScribeAddToHistory(data) {
				this.addToHistory(data);
			},
			onSonicScribeClearSelection() {
				this.selectedWordKeys = [];
			},
			onSonicScribeSelectByGroupKeys(keys) {
				this.selectedWordKeys.push(...keys);
			},
			onSonicScribeSelectWordsByRange(start, end) {
				let arr = this.visibleWords.filter(f => end > f.timelineStart && start < (f.timelineStart + f.Duration));
				this.selectedWordKey = arr.length ? arr.map(f => f.key) : null;

				if (__DEBUG__)
					console.debug('onSonicScribeSelectWordsByRange', { start, end, arr });
			},
			getGroupWords(group, words) {
				let start = group.startOffset || 0;
				let end = group.duration ? (start + group.duration) : this.buffer?.duration;
				return words.filter(w => (w.actualStart + w.Duration) > start && (!group.duration || w.actualStart < end));
			},
			isWordSelected(word) {
				return !!word && this.selectedWordKeys.includes(word.groupKey);
			},
			getSpeakerColor(speakerId) {
				let colors = ['#f27649', '#63B877', '#FFBE2A', '#3D428F', '#FA8170', '#2B9120', '#FFC60D', '#404DC7', '#FF7700', '#28249E', '#D1173C'];
				return this.speakers[speakerId]?.color || colors[speakerId % colors.length];
			},
			onSonicScribeSelectWord(val) {
				if (Array.isArray(val)) {
					this.selectedWordKeys = val.map(f => f.groupKey).filter(f => f);
				} else {
					this.selectedWordKeys = [val.groupKey];
				}
			},
			onSonicScribeTimeline(v, forceChange, opts) {
				if (opts?.clearSelection) {
					this.$eventsBus.$emit('sonic-scribe-clear-selection', null);
				}
				
				this.$playingTime = v;

				if (forceChange) {
					this.processPlayerTimeChange(v);
				}

				let playPercent = v / this.totalDuration * 100;

				if (this.$refs.playingTime) {
					this.$refs.playingTime.innerText = this.$Data.secondsToDisplayValue(Math.floor(v));
				}
				this.$refs.player.style.setProperty('--width', `${playPercent}%`);

				let playingWord = this.words.find(f => this.isWordPlaying(f, v));

				let el = document.querySelector('.word.playing');
				if (el) {
					el.classList.remove('playing');
				}

				if (playingWord) {
					el = document.querySelector(`#word_${playingWord.groupKey}`);
					if (el) {
						el.classList.add('playing');
					}
				}
			},
			apply(v) {
				let word = this.$lastWaveFormPositionWord;
				if (!word) return;

				let time = this.$refs.bottomPlayer.$refs.audioSrc.currentTime;
				if (!word.originalStart) {
					word.originalStart = word.Offset;
				}
				if (!word.originalDuration) {
					word.originalDuration = word.Duration;
				}
				switch(v) {
					case -1: {
						let lastOffset = word.Offset;
						word.Offset = Math.max(0, word.Offset + (time - this.debugMarkers[0].time));
						word.Duration += lastOffset - word.Offset;
						break;
					}
					case 1:
						word.Duration = Math.min(this.$debugBuffer?.duration, word.Duration + (time - this.debugMarkers[1].time));
						break;
					case 0:
						if (word.originalStart != null) {
							word.Offset = word.originalStart;
						}

						if (word.originalDuration != null) {
							word.Duration = word.originalDuration;
						}
						break;
				}

				this.setDebugMarkers(true);
			},
			gotoDebug(idx) {
				let debug = this.debugMarkers[idx];
				if (debug) {
					this.$refs.bottomPlayer.setPosition(debug.time);
				}
			},
			async nextPeak() {
				let buf = await audio.cut(this.$debugBuffer, this.$refs.bottomPlayer.$refs.audioSrc.currentTime, this.$debugBuffer.duration);
				let beatInfo = await calculateBPM(this.track.__tracker.audioContext, buf);
				if (__DEBUG__)
					console.debug('Beat info', { beatInfo });

				if (beatInfo?.firstPeak) {
					this.$refs.bottomPlayer.changePlay(beatInfo.firstPeak);
				}
			},
			moveAvg(dir) {
				this.$refs.bottomPlayer.changePlay(dir * this.avgWordLen);
			},
			round(v) {
				return Math.round(v * 1000)/1000 || 0;
			},
			async showPositionInAudio(pos) {
				let word = this.highlightedWords[0];

				if (!word) return;

				let start = Math.max(word.Offset - 2, 0);
				let end = Math.min(this.track.__buffer.duration, word.Offset + word.Duration + 2);

				let startDelta = word.Offset - start;

				if ((this.$lastWaveFormPositionWord !== word) || !this.$refs.bottomPlayer?.$refs?.audioSrc) {
					this.$debugBuffer = await audio.cut(this.track.__buffer, start, end);
					let data = await bufferToWave(this.$debugBuffer);
					this.debugAudio = data;
					this.$lastWaveFormPositionWord = word;
					this.$waveFormPosition = pos > 0 ? startDelta + word.Duration : startDelta;
					this.$waveFormStartDelta = startDelta;
					this.$waveFormStart = start;
					
					this.debugAudioTitle = `"${word.SentenceWord}" ${this.round(word.Offset)} - ${this.round(word.Offset + word.Duration)} (${this.round(word.Duration)}), AVG: ${this.avgWordLen}ms`;

					this.setDebugMarkers();
				} else {
					let wordStart = word.Offset - this.$waveFormStart;
					this.$refs.bottomPlayer.setPosition(pos > 0 ? wordStart + word.Duration : wordStart);
				}
			},
			async setDebugMarkers(repaint) {
				if (this.$lastWaveFormPositionWord && this.isDebug && this.$peaks) {
					let word = this.$lastWaveFormPositionWord;
					let wordIdx = this.words.indexOf(word);
					let prevWord = this.words[wordIdx - 1];
					let nextWord = this.words[wordIdx - 1];

					let prevWordDuration = Math.min(prevWord?.Duration || 999, Math.min(this.avgWordLen, word.Duration || 999)) * this.avgStep;
					let nextWordDuration = Math.min(nextWord?.Duration || 999, Math.min(this.avgWordLen, word.Duration || 999)) * this.avgStep;

					let wStart = word.Offset - this.$waveFormStart;
					let debugMarkers = [ 
						{ time: wStart, color: '#FFC107', width: 2, isTop: true },
						{ time: wStart + word.Duration, color: '#FFC107', width: 2, isTop: true },
						{ time: Math.max(0, wStart - this.avgWordLen * this.avgStep), color: '#FFFFFF', width: 1, isBottom: true },
						{ time: Math.min(this.$debugBuffer?.duration, wStart + word.Duration + this.avgWordLen * this.avgStep), color: '#FFFFFF', width: 1, isBottom: true },
					];

					this.debugMarkers = [];

					let getPeaks = (start, end, sortDir) => {
						const peaks = this.$peaks.filter(f => f.time > start && f.time < end).map(f => ({ ...f, value: Math.max(f.peak[0], Math.abs(f.peak[1])) })).filter(f => f.value > 0.0005);
						let maxPeak = _.max(peaks.map(f => f.value));
						let minPeak = _.min(peaks.map(f => f.value));
						let minPeaks = _.orderBy(peaks.filter(f => f.value >= minPeak && f.value <= Math.min(minPeak * 2, (minPeak + (maxPeak - minPeak) * 0.1))), ['time'], [sortDir]);
						let peak = minPeaks[0];

						if (__DEBUG__)
							console.debug(`Peaks info ${sortDir}`, { peaks, maxPeak, minPeak, peak, minPeaks });

						return peak;
					}

					if (debugMarkers[0]?.time !== debugMarkers[2]?.time) {
						let peak = getPeaks(debugMarkers[2]?.time, debugMarkers[0]?.time + prevWordDuration, 'desc');
						if (peak) {
							debugMarkers.push({ time: peak?.time, color: '#FF0000', width: 2, type: 'startPeak' });
						}
					}

					if (debugMarkers[1]?.time !== debugMarkers[3]?.time) {
						let peak = getPeaks(debugMarkers[1]?.time - nextWordDuration, debugMarkers[3]?.time, 'asc');
						if (peak) {
							debugMarkers.push({ time: peak?.time, color: '#FF0000', width: 2, type: 'endPeak' });
						}
					}

					let visibleWords = this.words.filter(f => !f.removed);
					let currentWordIdx = visibleWords.findIndex(f => f === word);

					if (currentWordIdx > -1) {
						let lastWord = visibleWords[currentWordIdx - 1];
						let nextWord = visibleWords[currentWordIdx + 1];

						let drawPos = (w, color) => {
							if (w) {
								let wordEndPos = (w.Offset + w.Duration) - this.$waveFormStart;
								if (wordEndPos > this.$debugBuffer?.duration) {
									wordEndPos = this.$debugBuffer?.duration;
								}

								let wordStartPos = w.Offset - this.$waveFormStart;
								if (wordStartPos < 0) {
									wordStartPos = 0;
								}

								debugMarkers.push({ time: wordStartPos, width: 1, isBottom: true, color: color, line: [wordStartPos, wordEndPos], word: w.SentenceWord })

								if (__DEBUG__)
									console.debug('Word pos', { wordEndPos, wordStartPos, w });
							}

						}

						drawPos(lastWord, '#9999FF');
						drawPos(nextWord, '#9999FF');
					}

					debugMarkers.push({ time: wStart, width: 3, isTop: true, color: '#FFC107', line: [wStart, wStart + word.Duration], preventTick: true, word: word.SentenceWord });

					if (word.originalStart != null && word.originalStart !== word.Offset) {
						debugMarkers.push({ time: word.originalStart - this.$waveFormStart, width: 2, isTop: true, color: '#00FFFF', preventTick: true, heightMP: 0.25 });
					}

					if (word.originalDuration != null && word.originalDuration !== word.Duration) {
						debugMarkers.push({ time: word.originalStart - this.$waveFormStart + word.originalDuration, width: 2, isTop: true, color: '#00FFFF', preventTick: true, heightMP: 0.25 });
					}

					this.debugMarkers = debugMarkers;

					if (repaint) {
						this.$nextTick(() => {
							this.$refs.bottomPlayer?.$refs?.waveForm?.waveform();
						});
					}
				}
			},
			playSelectedWord() {
				let word = this.highlightedWords[0];

				if (!word) return;

				let start = word.Offset - Math.max(word.Offset - 2, 0);

				this.$refs.bottomPlayer.setPosition(start);
				this.$refs.bottomPlayer.$refs.audioSrc.play();
				setTimeout(() => this.$refs.bottomPlayer.$refs.audioSrc.pause(), word.Duration * 1000);
			},
			onWaveformInitiated(peaks, segSize) {
				if (this.$waveFormPosition) {
					this.$refs.bottomPlayer.setPosition(this.$waveFormPosition);
					delete this.$waveFormPosition;
					let sampleRate = this.track.__tracker.audioContext.sampleRate;
					this.$peaks = _.orderBy(peaks.map((v, i) => ({
						peak: v,
						time: (~~(i * segSize)) / sampleRate
					})), (v) => Math.max(v.peak[0], Math.abs(v.peak[1])), ['asc']);
					this.setDebugMarkers(true);
				} else {
					this.debugAudioTitle = null;
				}
			},
			onWordDblClick(word) {
				if (__DEBUG__)
					console.debug('Dbl click', { word });

				this.clearHighlight();

				this.onSonicScribeSelectWord(word);
				this.editHighlightedWords();
			},
			saveSpeaker() {
				this.$set(this.speakers, this.speakerPopup.id, {
					name: this.speakerPopup.name,
					color: this.speakerPopup.color
				});
				this.speakerPopup.visible = false;

				if (this.$refs.bottomPanel) {
					this.$refs.bottomPanel.$drawImage('speaker');
				}
			},
			showSpeakerPopup(id) {
				this.speakerPopup.visible = false;
				this.speakerPopup.id = id;
				this.speakerPopup.name = this.getSpeakerName(id);
				this.speakerPopup.color = this.getSpeakerColor(id);
				this.$nextTick(() => this.speakerPopup.visible = true);
			},
			getSpeakerName(id) {
				return this.speakers[id]?.name || `${this.$tCaption('Speaker')} ${id + 1}`;
			},
			async showSpeechResponse() {
				const speech = (await this.track.__recognizeSpeech())?.RecognizedTranscript || [];
				speech[0].correction = await this.calcCorrectTimings(speech[0]?.Words || [], this.track.__buffer);
				this.$notifyMessage('Speech-to-text result', { JSON: speech, fullScreen: true, jsonExpandLevel: 2 });
			},
			async calcCorrectTimings(words) {
				let sourceWords = words.filter(f => !f.Filler);
				let wordsArray = sourceWords.map(f => ({
					time: f.Offset,
					duration: f.Duration
				}));

				this.isCorrectingTimings = true;
				let correctedWords = await correctWordTimings(wordsArray, this.track.__buffer, async (progress) => {
					this.correctingProgress = progress;
					if (__DEBUG__)
						console.debug('Progress', progress, this.isCorrectingTimings);
				}).finally(() => this.isCorrectingTimings = false);

				if (__DEBUG__)
					console.debug('correctedWords', { correctedWords });

				let diff = [];

				correctedWords.map((f, i) => {
					let sourceWord = sourceWords[i];
					if (sourceWord.Offset !== f.time || sourceWord.Duration !== f.duration) {
						diff.push({
							sourceWord,
							correctedWord: f
						});
					}
				});

				return {
					sourceWords,
					correctedWords,
					diff
				};
			},
			async fixWordsTimings(words) {
				let correction = await this.calcCorrectTimings(words, this.track.__buffer);

				this.addDebugInfo(`Corrected: ${(correction?.diff || []).map(f => 
					`${f.sourceWord.SentenceWord} (start - end): (old: ${this.round(f.sourceWord.actualStart)} - ${this.round(f.sourceWord.actualStart + f.sourceWord.Duration)}) ` +
					`(new: ${this.round(f.correctedWord.time)} - ${this.round(f.correctedWord.time + f.correctedWord.duration)}))`).join(', ')}`, []);

				(correction?.diff || []).map(f => {
					f.sourceWord.actualStart = f.sourceWord.Offset = f.correctedWord.time;
					f.sourceWord.Duration = f.correctedWord.duration;
				});

				if (__DEBUG__)
					console.debug(`Corrected ${correction?.diff?.length || 0} words`);
			},
			async showSpeechResponseCSV() {
				let words = this.words.filter(f => !f.CustomBuffer);
				let result = [];
				words.map((r, i) => {
					let data = { 
						word: r.SentenceWord, 
						start: this.round(r.Offset),
						end: this.round(r.Offset + r.Duration),
						duration: this.round(r.Duration),
						startFix: this.round(r.originalStart && (r.Offset - (r.originalStart || 0)) || 0),
						endFix: this.round(r.originalStart && ((r.Offset + r.Duration) - ((r.originalStart || 0) + (r.originalDuration || 0))) || 0)
					};
					if (i === 0) {
						result.push(Object.keys(data).join('\t'));
					}
					result.push(Object.keys(data).map(k => data[k]).join('\t'));
				});

				saveAs(new Blob([result.join('\r\n')]), 'words.csv');
			},
			downloadDebugAction(action, log) {
				if (!action) return;

				saveAs(action.audio, `${log.title} - ${action.name}.wav`);
			},
			executeDebugAction(action) {
				if (!action) return;

				if (action.audio) {
					this.debugAudio = null;
					this.$nextTick(() => {
						this.debugAudio = action.audio;
					});
				}
				if (action.text) {
					this.$notifyMessage(this.$Data.encodeHTML(action.text), { fullScreen: true });
				}
			},
			addDebugInfo(title, actions) {
				if (!this.isDebug) return;

				actions.map(async f => {
					if (f.buffer) {
						f.audio = await audio.bufferToWave(f.buffer)
					}
				});

				this.debugInfo.splice(0, 0, {
					time: new Date(),
					title,
					actions
				});
			},
			async onPlayerClick(e) {
				if (__DEBUG__)
					console.debug('onPlayerClick', e);

				this.processPlayerTimeChange(e.offsetX / this.$refs.player.clientWidth * this.totalDuration);
			},
			async processPlayerTimeChange(v, preventEvents) {
				let play = this.isPlaying;
				if (this.isPlaying) {
					await this.stop();
				}

				this.$nextTick(() => {
					this.$playingTime = v;
					if (!preventEvents) {
						this.$eventsBus.$emit('sonic-scribe-timeline', this.$playingTime);
					}

					const scroll = this.$refs.grid?.instance?.getScrollable();
					if (scroll) {
						let playingWord = this.words.find(f => f.timelineStart >= this.$playingTime);

						if (!playingWord) return;

						let sentenceIdx = this.sentences.findIndex(f => f === playingWord?.sentence);

						let charWidth = 17;
						let gridWidth = this.$refs.grid.$el.offsetWidth;
						let rowHeight = 25;
						let sentencesHeight = this.sentences.slice(0, playingWord.sentence.index).reduce((res, sentence) => {
							if (!sentence.removed) {
								let wordsWidth = sentence.words.filter(w => !w.remoed && !w.markToRemove).reduce((wres, word) => {
									wres += word.Word.length * charWidth;
									return wres;
								}, 0);

								res += Math.ceil(wordsWidth / gridWidth) * rowHeight + 15;
							}
							return res;
						}, 0);

						if (__DEBUG__)
							console.debug('onPlayerClick', { playingWord, sentenceIdx, sentencesHeight });
						scroll.scrollTo(sentencesHeight);
					}

					if (play) {
						this.play(void 0, true);
					}
				});
			},
			checkSentence(words) {
				let processed = [];

				words.map(f => {
					let sentence = f.sentence;
					if (!processed.includes(sentence)) {
						this.$set(sentence, 'removed', !sentence.words.some(f => !f.removed));
						processed.push(sentence);
					}
				});

				if (__DEBUG__)
					console.debug('checkSentence', { words });
			},
			onBodyMouseUp() {
				delete this.$highlightMode;
			},
			setHighlightMode(word, e) {
				if (__DEBUG__)
					console.debug('setHighlightMode', { word, e });

				let wordSelected = !!this.isWordSelected(word);

				if (e && !(e.altKey || e.shiftKey || e.ctrlKey)) {
					this.selectedWordKeys = [];
				}
				if (!word) {
					delete this.$highlightMode;
				} else {
					this.$highlightMode = !wordSelected;
					this.checkHighlight(word);

					if (this.$highlightMode) {
						this.$eventsBus.$emit('sonic-scribe-timeline', (word?.timelineStart || 0) + 0.0001);
						this.$eventsBus.$emit('sonic-scribe-select-word', word);
					}
				}
			},
			checkHighlight(word) {
				if (this.$highlightMode !== void 0) {
					if (this.selectedWordKeys.includes(word.groupKey) !== this.$highlightMode) {
						this.$Data.toggleSelectedIdInArray(word.groupKey, this.selectedWordKeys);
					}
				}
			},
			calcHeight() {
				if (this.heightAuto) {
					this.innerHeight = window.innerHeight - (this.$el.getBoundingClientRect()?.y || 0) - this.$el.offsetTop - this.bottomPanelHeightProp.value + 10;
				}
			},
			showOverdubbingWindow(mode) {
				this.overdubbingMode = mode;
				this.overdubbingVisible = false;
				this.overdubbingWord = this.highlightedWords[0]?.SourceWordsKeys?.length ? this.highlightedWords[0].SentenceWord : null;
				this.$nextTick(() => this.overdubbingVisible = true);
			},
			editHighlightedWords() {
				this.showOverdubbingWindow('replace');
			},
			async cutLimited(buffer, startIn, endIn, isStart) {
				let limitSeconds = 55;
				let limitSize = 1024 * 1024 * 4.5;

				let result = null;
				
				let start = startIn;
				let end = endIn;

				let processResult = async () => {
					if (isStart) {
						start = (end - start) > limitSeconds ? (endIn - limitSeconds) : start;
					} else {
						end = (end - start) > limitSeconds ? (startIn + limitSeconds) : end;
					}

					result = await audio.cut(buffer, start, end);

					if (__DEBUG__)
						console.debug('Cut limited', { buffer, start, end, result });

					return result;
				}

				await processResult();

				while (result?.length && result.length > limitSize) {
					limitSeconds /= 2;
					await processResult();
				}

				return result;
			},
			clearHighlight() {
				this.selectedWordKeys = [];
			},
			async processOverdub() {
				let hWord = this.highlightedWords[0];
				let group = hWord?.sentence?.group;
				let word = group.words.find(w => w.key === hWord?.key);
				let words = group.words.filter(f => this.isWordSelected(f));
				let newPhrase = this.overdubbingWord;
				let mode = this.overdubbingMode;
				let processingWords = [...words];

				await this.fixWordsTimings(processingWords);

				if (this.highlightedWords.length === 1 && mode === 'replace') {
					let cIdx = group.words.findIndex(w => w.key === word?.key);

					let wordBefore = group.words.filter((w, idx) => idx < cIdx && !w.removed && !w.markToRemove).slice(-1)[0];
					if (wordBefore && !wordBefore?.CustomBuffer && wordBefore.sentence?.key === word?.sentence?.key) {
						words.splice(0, 0, wordBefore);
						newPhrase = `${wordBefore.SentenceWord} ${newPhrase}`;
					}

					let wordAfter = group.words.filter((w, idx) => idx > cIdx && !w.removed && !w.markToRemove)[0];
					if (wordAfter && !wordAfter?.CustomBuffer && wordAfter.sentence?.key === word?.sentence?.key) {
						words.push(wordAfter);
						newPhrase = `${newPhrase} ${wordAfter.SentenceWord}`;
					}

				}

				let sourceWordsKeys = words.map(f => f.key);
				let sourceWord = word?.SourceWord && group.words.find(f => f.key === word.SourceWord?.key) || word;
				
				this.overdubbingVisible = false;
				this.clearHighlight();

				if (__DEBUG__)
					console.debug('processOverdub', { sourceWord, sourceWordsKeys, newPhrase });

				processingWords.map(f => this.$set(f, 'isProcessing', true));
				
				try {
					let originalWords = group.words.filter(f => !f.CustomBuffer);
					let findIdx = (w) => {
						let res = -1;
						let sw = w?.SourceWord && group.words.find(f => f.key === w.SourceWord?.key) || w;
						res = originalWords.findIndex(o => sw.key === o.key);
						
						if (res === -1) {
							res = group.words.findIndex(o => w.key === o.key);
						}

						return res;
					}
					let currentWordIndex = findIdx(words[0]);
					let lastWordIndex = findIdx(words.slice(-1)[0]);

					let findWord = (dir, idx) => {
						let counter = 0;
						let wIdx = idx !== void 0 ? idx : currentWordIndex;
						let words = [];
						while (originalWords[wIdx + dir]?.Speaker === word.Speaker && counter < 20) {
							if (dir < 0) {
								words.splice(0, 0, originalWords[wIdx + dir]);
							} else {
								words.push(originalWords[wIdx + dir]);
							}
							wIdx += dir;
							counter++;
						}
						return {
							word: originalWords[wIdx],
							words
						}
					};

					let sw = findWord(-1, currentWordIndex);
					let ew = findWord(1, lastWordIndex);
					let startWord = sw.word;
					let endWord = ew.word

					const startPos = startWord.Offset;
					const endPos = endWord.Offset + endWord.Duration;

					let wordStart = sourceWord.Offset;
					let wordEnd = sourceWord.Offset + sourceWord.Duration;

					if (__DEBUG__)
						console.log('Overdub', { currentWordIndex, startWord, endWord });

					const buffer = this.track.__buffer;

					const prefixBuffer = sourceWord.Offset > startWord.Offset ? await this.cutLimited(buffer, startWord.Offset, sourceWord.Offset, true) : null;
					const prefixText = sw.words.map(f => f.SentenceWord).join(' ');
					const prefixWav = prefixBuffer && await audioHelper.resampleBlob(await audio.bufferToWave(prefixBuffer), 24000, { mono: true });

					const suffixBuffer = wordEnd < endPos ? await this.cutLimited(buffer, wordEnd, endPos, false) : null;
					const suffixText = ew.words.map(f => f.SentenceWord).join(' ');
					const suffixWav = suffixBuffer && await audioHelper.resampleBlob(await audio.bufferToWave(suffixBuffer), 24000, { mono: true });

					const promises = [];

					if (prefixWav) {
						promises.push(this.$azure.uploadBlobWithInfoResponse(`overdubbing_${this.$Data.uuid()}.wav`, prefixWav, 'other'));
					} else {
						promises.push(Promise.resolve(null));
					}

					if (suffixWav) {
						promises.push(this.$azure.uploadBlobWithInfoResponse(`overdubbing_${this.$Data.uuid()}.wav`, suffixWav, 'other'));
					} else {
						promises.push(Promise.resolve(null));
					}

					if (!promises.length) return null;

					const azureFiles = await Promise.all(promises);
					const links = azureFiles.map(f => `${f?.AzureFileUrl}`);

					const language = this.creative.JsonConfig?.sonicScribe?.language || this.track?.tracker?.language || 'en-US';

					const prefixInfo = prefixWav && `prefixAudioContent="${this.$Data.encodeHTML(links[0])}" prefixText="${prefixText || 'none'}"` || '';
					const suffixInfo = suffixWav && `suffixAudioContent="${this.$Data.encodeHTML(links[1])}" suffixText="${suffixText || 'none'}"` || '';

					const tag = `<mstts:ttsembedding ${prefixInfo ? prefixInfo : ''} ${suffixInfo ? suffixInfo : ''} format="wav"><lang xml:lang="${language}"> ${newPhrase} </lang></mstts:ttsembedding>`;

					const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis"  xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="${language}">`
						+ `<voice xml:lang="${language}" name="en-US-ZeroshotOverDubNeural"> ${tag} </voice>`
						+ `</speak>`;

					const preparedSsml = ssmlHelper.prepareSsml(ssml, this.$Data.formatObjectToLowerCase(this.track.insertion));

					let insertion = {
						Oid: null,
						Type: 'SsmlInsertion',
						StartTime: 0,
						Ssml: preparedSsml.ssml,
						Language: preparedSsml?.language,
						Voice: preparedSsml?.voice,
						Rate: 1,
						Pitch: 1,
						JsonConfig: {},
						CreativeId: this.creative?.Oid
					}
					
					const result = await ssmlGenerator.generateSSMLAudio(this.track.__tracker, insertion, true);

					if (__DEBUG__)
						console.debug('Process overdub', { startPos, endPos, prefixBuffer, suffixBuffer, prefixText, suffixText, prefixWav, suffixWav, links, tag, ssml, result });

					let merged = await audio.merge([prefixBuffer, result.buffer, suffixBuffer], [0, Math.min(result.buffer.duration / 20, 0.1), Math.min(result.buffer / 20, 0.1)]);

					let resultBuffer = result.buffer;
					this.addDebugInfo(`OverDub "${newPhrase}"`, [
						{
							name: 'SSML',
							text: preparedSsml.ssml
						},
						{
							name: 'Prefix',
							audio: prefixWav
						},
						{
							name: 'Suffix',
							audio: suffixWav
						},
						{
							name: 'Result',
							buffer: resultBuffer
						},
						{
							name: 'Full',
							buffer: merged
						}
					]);

					let audioInfo = await audioHelper.getBufferInfo(resultBuffer);

					//normalize
					const gain = audioHelper.dbToGain(-3 - audioInfo.peakDb);
					let normalizedBuffer = await audio.processBuffer(resultBuffer, (source, ctx) => {
						if (__DEBUG__)
							console.debug('Normalization', { gain });
						let normalizeGain = ctx.createGain();
						normalizeGain.gain.value = gain;

						source.connect(normalizeGain);
						normalizeGain.connect(ctx.destination);

						return normalizeGain;
					});
					this.addDebugInfo(`OverDub normalization "${newPhrase}"`, [
						{
							name: 'Result',
							buffer: normalizedBuffer
						}
					]);

					let normalizedBlob = await bufferToWave(normalizedBuffer);

					//denoise
					const denoisedWav = await this.$Data.denoiseMedia({ blob: normalizedBlob, asBlob: true });
					let denoisedBuffer = await this.$Data.getAudioBufferFromBlob(denoisedWav, this.track.__tracker.audioContext);
					this.addDebugInfo(`OverDub denoised "${newPhrase}"`, [
						{
							name: 'Result',
							buffer: resultBuffer
						}
					]);

					//trim silence
					const silence = await audioHelper.detectSilence(denoisedBuffer, -60);

					if (silence?.start || silence?.end) {
						resultBuffer = await audio.cut(resultBuffer, silence.start || 0, resultBuffer.duration - (silence.end || 0));
					}

					let resultBlob = await bufferToWave(resultBuffer);

					if (silence?.start || silence?.end) {
						this.addDebugInfo(`OverDub "${newPhrase}" remove silence (-60db) (start: ${silence?.start || 0}s, end: ${silence?.end}s)`, [
							{
								name: 'Result',
								audio: resultBlob
							}
						]);
					}

					let lastW = words.slice(-1)[0];

					let newWord = {
						SentenceWord: newPhrase,
						Word: newPhrase,
						Offset: (['after', 'replace'].includes(mode) ? lastW.Offset + lastW.Duration : word.Offset),
						Duration: resultBuffer.duration,
						Speaker: word.Speaker,
						SourceWord: word,
						SourceWordsKeys: sourceWordsKeys,
						key: this.$Data.uuid()
					}

					if (word.CustomBuffer) {
						newWord = word;
						newWord.SentenceWord = newPhrase;
						newWord.Word = newPhrase;
						newWord.Duration = resultBuffer.duration;
						newWord.CustomBuffer = resultBuffer;
					}

					let customBuffer = resultBuffer;
					Object.defineProperty(newWord, 'CustomBuffer', { 
						get: () => customBuffer,
						set: (v) => customBuffer = v
					});

					if (!word.CustomBuffer) {
						Object.defineProperty(newWord, 'sentence', {
							get: () => word.sentence
						});

						if (this.structureStoreMode) {
							const audio = await this.$azure.uploadBlobWithInfoResponse(`sonicScribe_${this.creative?.Oid}_${this.$Data.uuid()}.wav`, resultBlob);
							newWord.AudioFile = audio;
							newWord.Insertion = {
								Ssml: preparedSsml.ssml,
								Language: preparedSsml?.language,
								Voice: preparedSsml?.voice
							};
						}
						
						if (['after', 'replace'].includes(mode)) {
							let lastW = words.slice(-1)[0];
							currentWordIndex = originalWords.findIndex(f => f.key === lastW.key);
						}
						let removeWordIndex = group.words.findIndex(f => f.key === originalWords[currentWordIndex + (['after', 'replace'].includes(mode) ? 1 : 0)].key);
						initializeSonicScribeGroupWord(group, newWord);
						group.words.splice(removeWordIndex, 0, newWord);

						let sentenceIdx = word.sentence.words.findIndex(w => w.key === word.key);
						word.sentence.words.splice(sentenceIdx + (['after', 'replace'].includes(mode) ? 1 : 0), 0, newWord);

						let affectedWords = [newWord];
						if (mode === 'replace') {
							if (word !== newWord) {
								affectedWords.push(...words);

								words.map(word => {
									this.$set(word, 'markToRemove', true);
									this.$set(word, 'removed', true);
									this.$set(word, 'replaced', newWord.key);
								});
							}
						}

						this.addToHistory({
							action: mode === 'replace' ? `"${word.SentenceWord}" ${this.$tCaption('replaced with')} "${newPhrase}"` : `"${newPhrase}" ${this.$tCaption('inserted')}`,
							affectedWords,
							confirmed: true,
							customBufferAction: true
						});
					}
				} finally {
					words.map(f => this.$set(f, 'isProcessing', false));
				}
			},
			async removeHighlightedWords() {
				let affectedWords = this.highlightedWords.map(w => {
					this.$set(w, 'markToRemove', true);
					this.$set(w, 'removed', true);
					return w;
				});

				this.fixWordsTimings(affectedWords);
				this.checkSentence(affectedWords);

				this.addToHistory({
					action: this.$tCaption('Removed') + ` "${affectedWords.map(f => f.SentenceWord).join(' ')}"`,
					affectedWords,
					confirmed: true
				});

				this.clearHighlight();
			},
			removeFillerWords() {
				let affectedWords = this.words.filter(f => f.Filler).map(w => {
					this.$set(w, 'markToRemove', true);

					return w;
				});

				if (!affectedWords.length) {
					this.$notifyWarning(this.$tCaption('No filler words detected'));
					return;
				}

				this.checkSentence(affectedWords);

				if (__DEBUG__)
					console.debug('removeFillerWords', { affectedWords });

				this.addToHistory({
					action: this.$tCaption('Remove filler words'),
					affectedWords,
					confirmed: false
				});
			},
			reset() {
				this.$confirm(void 0, this.$tCaption('All changes will be lost and the original state will be restored. Continue?')).then(() => {
					this.history = [];
					let wordsToRemove = [];
					this.groups.splice(1, 9999);
					this.sonicScribeInfo('groups').items.splice(1, 9999);

					this.groups[0].duration = null;
					this.groups[0].startOffset = 0;

					this.sonicScribeInfo('groups').items[0].startOffset = 0;
					this.sonicScribeInfo('groups').items[0].duration = null;

					let resetWord = (w) => {
						this.$set(w, 'removed', false);
						this.$set(w, 'markToRemove', false);
						this.$set(w, 'isProcessing', false);
						this.$set(w, 'startOffset', 0);
						this.$set(w, 'replaced', null);
					}

					this.groups.map(group => {
						group.words.map((w, idx) => {
							resetWord(w);
							
							if (w.AudioFile) {
								wordsToRemove.splice(0, 0, idx);
							}
						});
					});

					this.sentences.map(f => this.$set(f, 'removed', false));

					wordsToRemove.map(idx => {
						let word = this.groups[0].words[idx];
						this.groups[0].words.splice(idx, 1);

						let sentenceIdx = word.sentence.words.findIndex(f => f.key === word.key);
						if (sentenceIdx > -1) {
							word.sentence.words.splice(sentenceIdx, 1);
						}
					});

					if (__DEBUG__)
						console.debug('Words to remove', { wordsToRemove });
				});
				this.checkSentence(this.words);
			},
			isWordPlaying(word, time) {
				return !word.removed && !word.markToRemove && time > 0 && time >= word.timelineStart && time <= (word.timelineStart + word.Duration);
			},
			removeWord(word) {
				this.$set(word, 'removed', true);
				this.addToHistory({
					action: `"${word.SentenceWord}" ${this.$tCaption('(removed)')}`,
					affectedWords: [word],
					confirmed: true
				});

				this.clearWordSelection(word);
				this.checkSentence([word]);
			},
			clearWordSelection(word) {
				let idx = this.selectedWordKeys.indexOf(word.groupKey);
				if (idx > -1) {
					this.selectedWordKeys.splice(idx, 1);
				}
			},
			removeSentence(sentence) {
				sentence.words.map(word => this.$set(word, 'removed', true));
				
				this.addToHistory({
					action: `"${sentence.words.map(f => f.SentenceWord).join(' ')}" ${this.$tCaption('(removed)')}`,
					affectedWords: sentence.words,
					confirmed: true
				});

				this.checkSentence(sentence.words);
			},
			getShortened(v) {
				return (v || '').substr(0, 50) + (v?.length > 50 ? '..' : '');
			},
			async initSpeechSentences() {
				let group = {
					id: this.$Data.uuid(),
					offset: 0
				};

				const speech = (await this.track.__recognizeSpeech())?.RecognizedTranscript || [];
				let words = speech.reduce((arr, sent) => {
					const splittedSentence = sent.Text?.split(' ');
					arr.push(...sent.Words.map((f, i) => ({
						...f,
						SentenceWord: splittedSentence[i],
						actualStart: f.Offset
					})))
					return arr;
				}, []);

				const responseTitle = await this.$Data.postExternalApi('OpenAiGpt3/Run', {
					promptCode: 'SONICSCRIBETITLE',
					parameters: {
						'SCRIPT': speech.map(f => f.Text).join('. ')
					},
					clientIp: this.$auth.userIP()
				});

				if (responseTitle?.Data?.Content) {
					this.creative.Name = `${this.$tCaption('SonicScribe', '@actions')}: ${responseTitle?.Data?.Content}`;
					this.creative.__save();
				}

				const sentences = [];
				let currentSentence = {
					speaker: null,
					words: [],
					key: `s${sentences.length + 1}`,
					index: sentences.length
				};
				Object.defineProperty(currentSentence, 'group', {
					get: () => group
				});
				words.map((w, wIdx) => {
					w.key = `w${wIdx + 1}`;
					currentSentence.words.push(w);

					if (currentSentence.speaker === null) {
						currentSentence.speaker = w.Speaker;
					}

					let sentence = currentSentence;
					Object.defineProperty(w, 'sentence', {
						get: () => sentence
					});
					if (w.SentenceWord?.substr(-1)?.match(/[.\!\?]/g) || w.Speaker !== currentSentence.speaker) {
						sentences.push(currentSentence);
						currentSentence = {
							speaker: null,
							words: [],
							key: `s${sentences.length + 1}`,
							index: sentences.length
						};
					}
				});

				if (currentSentence.words.length > 0) {
					sentences.push(currentSentence);
				}

				group.structure = sentences;

				this.sonicScribeInfo('groups', {
					items: [group]
				});
			},
			async init() {
				this.disabled = true;
				this.isLoading++;
				try {
					if (!this.sonicScribeInfo('groups')) {
						if (this.track?.insertion?.JsonConfig?.sonicScribe?.structure) {
							//BACK COMPATIBILITY WITH VERSIONS BEFORE AUG 2024
							let result = await initStructureSentences(this.track?.insertion?.JsonConfig?.sonicScribe?.structure, this.track.__tracker.audioContext);

							let group = {
								id: this.$Data.uuid(),
								offset: 0,
								structure: result.sentences
							}

							this.sonicScribeInfo('groups', {
								items: [group]
							});
						} else {
							await this.initSpeechSentences();
						}
					}

					await initGroups(this.sonicScribeInfo('groups'), this.track.__tracker.audioContext);
					await this.build();
				} finally {
					this.disabled = false;
					this.isLoading--;
				}
			},
			wait(time) {
				return new Promise(res => setTimeout(res, time));
			},
			simplifyWord(w) {
				if (!w?.match || w.match(/\<(.*?)\>(.*?)\<\/(.*?)\>/g)) {
					return '';
				}
				return (w || '').replace(/\<(.*?)\>/g, '').replace(/[,. !?;+-=\t\n\r]/g, '').toLowerCase();
			},
			searchGoodStart(phrase, arr, arrStartIdx) {
				arr = arr || 'Test sentence. Great sentence. Single word. Good job. Test sentence 2. Good sentence. Great job.'.split(' ');
				let pArr = Array.isArray(phrase) ? phrase : phrase.split(' ');

				if (!pArr.length) return -1;

				let startWord = pArr[0];
				let variants = arr.slice(arrStartIdx, arr.length).map((f, i) => this.simplifyWord(startWord) === this.simplifyWord(f) ? { idx: i + arrStartIdx } : null).filter(f => f !== null);

				if (variants.length) {
					variants.map(f => {
						let vIdx = f.idx;
						let sArr = arr.slice(vIdx, arr.length);

						let words = pArr.slice(1, pArr.length).map(w => {
							let sVariants = sArr.map((f, i) => this.simplifyWord(w) === this.simplifyWord(f) ? { idx: i + vIdx, lIdx: i + 1, w } : null).filter(f => f !== null);

							return sVariants;
						});

						f.words = [{ idx: vIdx, lIdx: 0 }, ...words];
						f.foundWords = words.filter(f => f.length);
						f.foundWordsLen = f.foundWords.length;
						f.totalLength = f.foundWords.reduce((res, item) => {
							res += item[0].lIdx || 0;
							return res;
						}, 0);
					});

					let sortedVariants =  _.orderBy(variants, ['foundWordsLen', 'totalLength'], ['desc', 'asc']);

					if (__DEBUG__)
						console.debug('Variants', { sortedVariants, startWord });
					
					return sortedVariants[0];
				} else {
					return variants[0];
				}
			},
			async processGPT() {
				if (!this.words.length || !this.command) return;

				const call = {
					command: this.command,
					affectedWords: [],
					confirmed: false
				}

				this.isProcessing++;
				try {
					const sourceWords = this.words.filter(f => !f.removed && !f.markToRemove);
					const words = sourceWords.map(f => ({
						word: f.SentenceWord,
						timestamp: f.Offset,
						speaker: f.Speaker
					}));

					const text = this.sentences.map(f => f.words.map(w => w.SentenceWord).join(' ')).join('\n'); // sourceWords.map(f => f.SentenceWord).join(' ');

					const response = await this.$Data.postExternalApi('OpenAiGpt3/Run', {
						promptCode: 'AUDIOTEXTSUMMARY',
						parameters: {
							'COMMAND': this.command,
							'SCRIPT': text
						},
						clientIp: this.$auth.userIP()
					});

					let editedText = (response?.Data?.Content || '');

					/**** FOR TESTS
					let response = null;
					//let editedText = 'Ik heb heel veel hard gelopen vroeger en op een gegeven moment is dat wat lastiger met je knieen';
					//let editedText = 'Waarom is dat zo belangrijk, vindt u?\nHet is nooit te laat om iets nieuws te leren.\nMaar ja, uiteindelijk is het ook een ander soort sport, maar wel ook heel gezond voor je.';					
					//let editedText = 'Dat was niet voor.\nIk heb heel veel hard gelopen vroeger en op een gegeven moment is dat wat lastiger met je knieen';
					//let editedText = 'Maar ja, uiteindelijk is het ook een ander soort sport, maar wel heel gezond voor je.';
					//let editedText = 'Waarom is dat zo belangrijk, vindt u?\nHet is nooit te laat om iets nieuws te leren.\nMaar ja, uiteindelijk is het ook een ander soort sport, maar wel ook heel gezond voor je.';
					let editedText = 'Er zijn zoveel sporten die je kan doen. Dat betekent niet dat je niet kan sporten, maar dat je een andere sport moet ontdekken waar je beter in bent.'
						+ ' Het is nooit te laat om iets nieuws te leren. Zelfs op mijn vijftigste heb ik ook een nieuwe sport geleerd.'
						+ ' Ik heb heel veel hard gelopen vroeger en nu haal ik heel veel plezier uit mountainbiken.'
					****/

					this.addDebugInfo(`GPT response for "${this.command}"`, [
						{
							name: 'Response',
							text: editedText
						},
					]);

					let replacedText = editedText.replace(/\<(.*?)\>(.*?)\<\/(.*?)\>/g, '').replace(/\s\s+/g, ' ').trim();
					if (replacedText.length) {
						editedText = replacedText;
					}

					if (!replacedText) {
						replacedText = editedText.replace(/\<(.*?)\>|<\/(.*?)\>/g, '').replace(/\s\s+/g, ' ').trim();
						if (replacedText.length) {
							editedText = replacedText;
						}
					}

					const editedSentences = editedText.split(/[.?!\n]/g).map(f => `${f}`.trim()).filter(f => f);

					if (editedSentences.length) {
						let lastEndIdx = null;
						let searchWords = sourceWords.map(f => f.SentenceWord);

						editedSentences.map(sentence => {							
							let variant = this.searchGoodStart(sentence, searchWords, lastEndIdx);
							let goodStartIdx = variant?.idx || 0;
							let sentenceWords = sentence.split(' ');

							if (goodStartIdx > -1) {
								for(let idx = lastEndIdx || 0; idx < goodStartIdx; idx++) {
									this.$set(sourceWords[idx], 'markToRemove', true);
									this.$set(sourceWords[idx], 'removed', false);
									call.affectedWords.push(sourceWords[idx]);
								}
							}

							let variantIndicies = [];
							(variant?.foundWords || []).map(f => variantIndicies.push(f[0]?.idx));

							let maxIdx = _.max(variantIndicies) || 0;

							if (maxIdx > goodStartIdx) {
								let currentIdx = 0;
								for(let idx = goodStartIdx || 0; idx <= maxIdx; idx++) {
									let currentWord = sentenceWords[currentIdx];
									
									if (this.simplifyWord(currentWord) === this.simplifyWord(sourceWords[idx]?.SentenceWord)) {
										currentIdx++;
									} else {
										this.$set(sourceWords[idx], 'markToRemove', true);
										this.$set(sourceWords[idx], 'removed', false);
										call.affectedWords.push(sourceWords[idx]);
									}
								}
							}

							lastEndIdx = Math.max(goodStartIdx, maxIdx || 0) + 1;
						});

						if (lastEndIdx < sourceWords.length - 1) {
							for(let idx = lastEndIdx; idx < sourceWords.length; idx++) {
								this.$set(sourceWords[idx], 'markToRemove', true);
								this.$set(sourceWords[idx], 'removed', false);
								call.affectedWords.push(sourceWords[idx]);
							}
						}
					}

					if (__DEBUG__)
						console.debug('ProcessGPT', { response, call, words, editedSentences, editedText, replacedText, text });
					
					if (call.affectedWords?.length) {
						this.addToHistory(call);
						this.checkSentence(call.affectedWords);
					} else {
						this.$notifyWarning(this.$tCaption('Request cannot be processed.'));
					}
				} finally {
					this.isProcessing--;
					this.command = null;
				}
			},
			deleteChange(item, itemIdx) {
				if (item.affectedWords) {
					item.affectedWords.map(f => f.markToRemove = false);

					if (item.customBufferAction) {
						item.affectedWords.filter(f => f.CustomBuffer).map(f => this.words.findIndex(w => w === f)).reverse().map(f => this.words.splice(f, 1));
						item.affectedWords.filter(f => f.CustomBuffer).map(f => f.sentence.words.splice(f.sentence.words.findIndex(w => w === f), 1));
						item.affectedWords.filter(f => f.replaced).map(f => f.replaced = false);
					}
					this.checkSentence(item.affectedWords);
				}
				this.history.splice(itemIdx, 1);
			},
			revertChange(item, itemIdx) {
				item.confirmed = false;
				if (item.affectedWords) {
					item.affectedWords.map(f => f.removed = false);
					item.affectedWords.map(f => f.replaced = null);
					this.checkSentence(item.affectedWords);
				}
				if (item.action || item.type) {
					this.deleteChange(item, itemIdx);
				}

				if (item.type === 'group') {
					if (item.groupOffsets) {
						Object.keys(item.groupOffsets).map(groupId => {
							let revertedOffsets = Object.keys(item.groupOffsets[groupId]).reduce((res, v) => {
								res[v] = item.groupOffsets[groupId][v] * -1;
								return res;
							}, {});
							this.$eventsBus.$emit('sonic-scribe-group-offset-changed', groupId, revertedOffsets);
						});
					}
				}

				if (item.type === 'word') {
					if (item.changesDelta) {
						let group = this.groups.find(f => f.id === item.groupId);
						if (!group) return;

						let word = group.words.find(f => f.key === item.wordKey);
						if (!word) return;


						Object.keys(item.changesDelta).map(prop => {
							word[prop] -= item.changesDelta[prop];
						});
					}
				}
			},
			confirmChange(item) {
				item.confirmed = true;
				item.affectedWords.map(f => f.removed = true);
				this.checkSentence(item.affectedWords);
			},
			setTimer(v) {
				if (this.$playingTimer) {
					clearInterval(this.$playingTimer);
					delete this.$playingTimer;

					clearInterval(this.$playingTimerLong);
					delete this.$playingTimerLong;
				}
				if (v) {
					this.$playingTime = Math.min(this.$playingTime, this.totalDuration);
					this.$eventsBus.$emit('sonic-scribe-timeline', this.$playingTime);

					this.$playingTimer = setInterval(() => {
						let v = Math.min(this.totalDuration, this.track.__tracker.audioContext.currentTime - this.playStarted);
						this.$eventsBus.$emit('sonic-scribe-timeline', v);
					}, 1000 / 144);

					this.$playingTimerLong = setInterval(() => {
						this.scrollToPlaying();
					}, 700);
				}
			},
			async play(sentence, preventBuild) {
				if (__DEBUG__)
					console.debug('Play');

				await this.stop();

				const tracker = this.track.__tracker;
				const ctx = await tracker.initAudioContext();
				
				if (!preventBuild || !this.$playingData) {
					this.$playingData = await this.build();
				}

				let offset = this.$playingTime || 0;

				if (!sentence && this.highlightedWords?.length) {
					sentence = this.highlightedWords[0]?.sentence;
				}

				if (sentence) {
					this.$playingTime = 0;
					this.$eventsBus.$emit('sonic-scribe-timeline', 0);

					let sentenceWord = sentence.words.find(f => !f.removed && !f.markToRemove && this.isWordSelected(f)) || sentence.words.find(f => !f.removed && !f.markToRemove);

					if (__DEBUG__)
						console.debug('Sentence play', { sentenceWord });

					if (sentenceWord) {
						offset = sentenceWord.timelineStart;
					}
				}

				if (this.$playingData.buffer?.duration - offset < 1) {
					offset = 0;
					this.playingTime = 0;
				}

				let tracks = [];
				let trackId = this.track && this.track.id || this.$Data.uuid();
				
				tracks.push({
					id: trackId,
					playbackRate: this.track.playbackRate,
					pitchRate: this.track.pitchRate,
					startOffset: offset
				});

				let buffer = this.$playingData.buffer;
				let trackBuffers = {};
				trackBuffers[trackId] = buffer;
				
				let playingSessionId = this.$Data.uuid();
				this.$playingSessionId = playingSessionId;
				this.isPlaying = true;
				this.playStarted = ctx.currentTime - offset;
				this.setTimer(true);

				this.track.__tracker.play((this.track && this.track.start || 0), {
					trackBuffers,
					tracks,
					duration: buffer.duration,
					solo: true,
					forcePlayTracks: [trackId],
					onStop: () => {
						if (this.$playingSessionId === playingSessionId) {
							this.$source = null;
							this.stop();
						}
					}
				});
			},
			stop() {
				this.setTimer(false);
				this.isPlaying = false;
				this.track.__tracker.stop();
			},
			getHash(s) {
				return s.split('').reduce(function(a, b) {
					a = ((a << 5) - a) + b.charCodeAt(0);
					return a & a;
				}, 0);
			},
			async build(force) {
				let groupsInfo = JSON.stringify(this.groups.map(f => ({ a: f.duration, b: f.startOffset, c: f.offset })));
				let hash = this.getHash(groupsInfo + JSON.stringify(getWordsForHash(this.words)));

				if (__DEBUG__)
					console.debug('Build', { force, hash, lasHash: this.$lastBuildHash, gr: [...this.groups] });

				if (hash === this.$lastBuildHash && force !== true) {
					if (__DEBUG__)
						console.debug('Build hash');
					return this.$lastBuildResult;
				}
				
				this.isBuilding = true;
				
				let cb = null;
				let totalDelta = 0;
				let totalStartOffset = 0;
				if (this.isDebug) {
					cb = (buffer, startOffset, start, end, part, hint, delta) => {
						delta = parseInt(delta * 1000)/1000 || 0;
						totalStartOffset += Math.round(startOffset * 1000)/1000 || 0;
						this.addDebugInfo(`Merge (part #${part}${hint ? ` ${hint}` : ''}): ${parseInt(start * 1000)/1000}s - ${parseInt(end * 1000)/1000}s`
							+ `, startOffset: ${Math.round(startOffset * 1000)}ms`
							+ `${delta ? `, duration delta: ${parseInt(delta * 1000)/1000}s` : ''}`
							+ `${totalDelta + totalStartOffset != 0 ? ` (actual time: ${parseInt((start + totalDelta - totalStartOffset) * 1000)/1000}s - ${parseInt((end + totalDelta - totalStartOffset + delta) * 1000)/1000}s)` : ''}`,
						[
							{
								name: 'Audio',
								buffer: buffer
							}
						]);
						totalDelta += delta || 0;
					};
				}
				//let result = this.$lastBuildResult = await build(this.track.__buffer, this.words, cb).finally(() => this.isBuilding = false);
				let result = this.$lastBuildResult = await buildGroups(this.track.__buffer, this.groups).finally(() => this.isBuilding = false);
				this.totalWords = result?.totalWords;
				this.totalDuration = result?.totalDuration;

				this.$lastBuildHash = hash;

				this.buffer = result?.buffer;

				return result;
			},
			async getWave(buffer) {
				return await bufferToWave(buffer || (await this.build()).buffer);
			},
			async download(buffer) {
				this.isLoading++;
				try {
					buffer = buffer || (await this.build()).buffer;

					let tracks = [];
					let trackId = this.track && this.track.id || this.$Data.uuid();
					
					tracks.push({
						id: trackId,
						playbackRate: this.track.playbackRate,
						pitchRate: this.track.pitchRate
					});

					let trackBuffers = {};
					trackBuffers[trackId] = buffer;

					await this.track.__tracker.downloadFinalAudio({
						trackBuffers,
						tracks
					});
				} finally {
					this.isLoading--;
				}
			},
			scrollToPlaying() {
				const scroll = this.$refs.grid?.instance?.getScrollable();
				if (scroll) {
					let positionBefore = scroll.scrollTop();
					scroll.scrollToElement(document.querySelector('.word.playing'));
					this.$nextTick(() => {
						if (scroll.scrollTop() > positionBefore) {
							scroll.scrollBy(200);
						}
					})
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.creative--text-based-audio-editor {
		.sentence {
			padding-bottom: 1px;
			position: relative;
			user-select: none;

			.speaker {
				font-size: 18px;
				font-weight: 100;
				color: var(--speaker-color);

				&:hover {
					color: $base-accent;
				}
			}

			.word {
				font-size: 18px;
				font-weight: 100;
				position: relative;
				user-select: none;
				cursor: pointer;

				&.custom {
					font-style: italic;
				}

				&.highlighted {
					color: $base-accent;
				}

				&.mark-to-remove {
					text-decoration: line-through;
					color: #cc0000;
				}
				
				&:hover {
					color: #ff7979;
				}

				&.playing{
					color: $base-accent;

					&::after {
						content: "";
						position: absolute;
						left: -4px;
						right: -4px;
						top: -2px;
						bottom: -2px;
						border: 2px solid $base-accent;
						border-radius: 4px;
					}
				}
			}

			.words {
				z-index: 1;
				padding-right: 40px;
			}

			.sentence-action-holder {
				position: absolute;
				left: -14px;
				top: -14px;
				right: -14px;
				bottom: -14px;
			}

			.sentence-actions {
				display: none;
				position: absolute;
				top: 10px;
				right: 2px;
				z-index: 2;
			}

			&:hover {
				.sentence-actions {
					display: flex;
				}
			}
		}

		.commands-panel {
			border-left: 1px solid #00000011;
			padding-left: 14px;
			max-width: 350px;
		}

		.text-panel {
			padding: 4px;
		}

		.send-button {
			width: 34px;
			height: 34px;
			background: #f5f5f5;
			border-radius: 4px;
		}

		.action-button {
			background: #fafafa;
			border: 2px solid #fafafa;
			border-radius: 4px;
			padding: 4px 8px;
			user-select: none;

			&.error {
				color: #ff7979;
				&:hover {
					border-color: #ff7979;
				}
			}

			&.success {
				color: #7dca83;
				&:hover {
					border-color: #7dca83;
				}
			}

			&.grey {
				color: #999;
				&:hover {
					color: #333;
				}
			}
		}

		.creative--text-based-audio-editor--player {
			height: 14px;
			border-radius: 6px;
			background: #dedede;
			position: relative;
			overflow: hidden;
			cursor: pointer;
			margin-left: 4px;
			margin-right: 4px;

			.player-line {
				position: absolute;
				width: var(--width);
				height: 100%;
				left: 0px;
				top: 0px;
				background: $base-accent;
			}

			.timers {
				position: absolute;
				right: 5px;
				top: 1px;
				font-size: 9px;
				color: #00000077;
				pointer-events: none;
				user-select: none;
			}
		}

		.debug-action {
			padding: 2px 4px;
			background: #e1e1e1;
			border-radius: 2px;
		}

		.debug-log {
			//border-bottom: 1px solid #e1e1e1;
			padding: 4px;
			background: #eeeeee;
			border-radius: 2px;
		}

		.small-input {
			height: 20px;
		}
	}
</style>
